import type {
  ApiError,
  ApiErrorBody,
  AppState,
  HoldingCsvRow,
  PricedHolding,
} from './types';

export const state: AppState = {
  rows: [],
  results: [],
  expandedHoldings: new Set<string>(),
};

export const knownIsins: Record<string, string> = {
  IE00B579F325: 'SGLP.L',
  IE00B4ND3602: 'SGLN.L',
  IE00BKWQ0F09: 'ENGE.L',
  IE00BQQP9F84: 'GDGB.L',
  IE00BQQP9G91: 'GJGB.L',
};

export const knownTickers: Record<string, string> = {
  SGLP: 'SGLP.L',
  SGLN: 'SGLN.L',
  ENGE: 'ENGE.L',
  GDGB: 'GDGB.L',
  GJGB: 'GJGB.L',
  YCA: 'YCA.L',
  JGB1X: 'JGB1X.XD',
  JGBX1: 'JGB1X.XD',
};

export const knownAssetClasses: Record<string, string> = {
  JGB1X: 'Bonds',
  JGBX1: 'Bonds',
  IE0000PJLVN3: 'Bonds',
};

export const colours = [
  '#111827',
  '#3b82f6',
  '#d97706',
  '#059669',
  '#7c3aed',
  '#dc2626',
  '#0891b2',
  '#6b7280',
] as const;

export function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element #${id}`);
  return element as T;
}

export function setDiagnostics(value: unknown): void {
  byId<HTMLElement>('diagnosticsText').textContent =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

export function parseCsv(text: string): HoldingCsvRow[] {
  const lines: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quote = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const next = text[index + 1];

    if (character === '"' && quote && next === '"') {
      cell += '"';
      index += 1;
    } else if (character === '"') {
      quote = !quote;
    } else if (character === ',' && !quote) {
      row.push(cell.trim());
      cell = '';
    } else if ((character === '\n' || character === '\r') && !quote) {
      if (character === '\r' && next === '\n') index += 1;
      row.push(cell.trim());
      if (row.some((value) => value !== '')) lines.push(row);
      row = [];
      cell = '';
    } else {
      cell += character;
    }
  }

  if (cell || row.length > 0) {
    row.push(cell.trim());
    lines.push(row);
  }

  if (lines.length < 2) throw new Error('The CSV is empty or has no data rows.');

  const headerRow = lines[0];
  if (!headerRow) throw new Error('The CSV header is missing.');
  const headers = headerRow.map((header) => header.replace(/^\uFEFF/, '').trim());

  return lines.slice(1).map((values) =>
    Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ''])) as HoldingCsvRow,
  );
}

export function parseNumber(value: unknown): number | null {
  const text = String(value ?? '').trim();
  if (text === '') return null;
  const parsed = Number(text.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

export function escapeCsv(value: unknown): string {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character] ?? character);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function resolveSymbol(row: HoldingCsvRow): string {
  const isin = (row.ISIN ?? '').trim().toUpperCase();
  const ticker = (row.Ticker ?? '').trim().toUpperCase();
  if (isin && knownIsins[isin]) return knownIsins[isin];
  if (ticker && knownTickers[ticker]) return knownTickers[ticker];
  if (ticker.includes('.') || ticker.includes('=') || ticker.includes('-')) return ticker;
  return ticker ? `${ticker}.L` : '';
}

export function resolveAssetClass(row: HoldingCsvRow): string {
  const isin = (row.ISIN ?? '').trim().toUpperCase();
  const ticker = (row.Ticker ?? '').trim().toUpperCase();
  return (knownAssetClasses[isin]
    ?? knownAssetClasses[ticker]
    ?? (row.AssetClass ?? 'Other').trim())
    || 'Other';
}

export function holdingKey(row: PricedHolding): string {
  return (row.ISIN ?? '').trim().toUpperCase()
    || row.symbol
    || (row.Ticker ?? '').trim().toUpperCase()
    || (row.Name ?? '').trim().toUpperCase();
}

export function displayHoldingName(row: PricedHolding): string {
  let name = (row.Name ?? row.Ticker ?? row.ISIN ?? '').trim();
  const ticker = (row.Ticker ?? '').trim();
  const resolved = row.symbol.trim();
  const candidates = [ticker, resolved, resolved.split('.')[0]].filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    name = name.replace(new RegExp(`\\s*\\(${escapeRegExp(candidate)}\\)\\s*$`, 'i'), '').trim();
  }
  return name;
}

export function money(value: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(value: number, maximumFractionDigits = 4): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits }).format(value);
}

export function portfolioPercentage(value: number, total: number): string {
  return total > 0 && Number.isFinite(value) ? `${((value / total) * 100).toFixed(2)}%` : '—';
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function asApiError(error: unknown): ApiError {
  return error instanceof Error ? error as ApiError : new Error(String(error));
}

export async function api<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { accept: 'application/json' } });
  const text = await response.text();
  let body: unknown;

  try {
    body = JSON.parse(text);
  } catch {
    const isHtml = /^\s*<!doctype html|^\s*<html/i.test(text);
    body = {
      error: isHtml
        ? 'The API route returned the web page instead of JSON. Deploy the Worker project rather than static assets only.'
        : 'The API returned a non-JSON response.',
      raw: text,
    } satisfies ApiErrorBody;
  }

  const possibleError = body as Partial<ApiErrorBody>;
  if (!response.ok || typeof possibleError.error === 'string') {
    const error = new Error(possibleError.error ?? `API returned ${response.status}`) as ApiError;
    error.details = body;
    error.status = response.status;
    throw error;
  }

  return body as T;
}

export function showError(message: string): void {
  const box = byId<HTMLElement>('errorBox');
  box.textContent = message;
  box.classList.remove('hidden');
}

export function hideError(): void {
  byId<HTMLElement>('errorBox').classList.add('hidden');
}
